/* PCIS Dashboard — route /supply/pcis-health/dashboard.

   The leadership view of Direct Integration: how much of Prozo's shipping runs on
   our own courier integrations, whether the volume is growing, and what's breaking.
   Five tabs over ONE filter rail — every /health/* endpoint takes the identical
   filter set, so a courier picked on Overview stays picked in Order Explorer.

   Deliberately additive: Summary, Details and Manage are untouched and still
   linked in the sidebar. This page combines and extends them rather than
   replacing them, so nobody's bookmark breaks.

   Tab state lives in the URL hash (#couriers), so a specific view can be shared or
   refreshed without landing back on Overview. */
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Tab, Tabs, Typography} from '@mui/material'
import PageTitle from '../../../../components/PageTitle'
import {PageHeader} from '../../components'
import {useListPcisCourierAccountsQuery} from '../../../../redux/supplyApiSlice'
import DashboardFilters, {apiFilters, defaultFilters} from './DashboardFilters'
import TabOverview from './TabOverview'
import TabCouriers from './TabCouriers'
import TabFailures from './TabFailures'
import TabLifecycle from './TabLifecycle'
import TabOrders from './TabOrders'
import {INDIGO} from './parts'

const TABS = [
  {key: 'overview', label: 'Overview'},
  {key: 'couriers', label: 'Couriers & Accounts'},
  {key: 'failures', label: 'Failures'},
  {key: 'lifecycle', label: 'Delivery Lifecycle'},
  {key: 'orders', label: 'Order Explorer'},
]

/* Auto-refresh cadence. Matched to the backend's 60s aggregate cache — polling
   faster would just re-serve the same cached payload. */
const POLL_MS = 60_000

/* Trend granularity. 'auto' lets the backend choose from the window length, which
   is right almost always; the explicit options are for when someone wants to see
   an incident hour by hour inside a week-long window. */
const BUCKETS = [
  {key: '', label: 'Auto'},
  {key: 'HOUR', label: 'Hourly'},
  {key: 'DAY', label: 'Daily'},
  {key: 'WEEK', label: 'Weekly'},
]

const tabFromHash = () => {
  const h = (window.location.hash || '').replace('#', '')
  return TABS.some((t) => t.key === h) ? h : 'overview'
}

function BucketPicker({value, onChange}) {
  return (
    <Stack direction='row' alignItems='center' spacing={0.75}>
      <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>
        Trend
      </Typography>
      <Box sx={{display: 'inline-flex', border: 1, borderColor: 'divider', borderRadius: '8px', overflow: 'hidden'}}>
        {BUCKETS.map((b) => (
          <Box
            key={b.key || 'auto'}
            component='button'
            type='button'
            onClick={() => onChange(b.key)}
            sx={{
              border: 0,
              cursor: 'pointer',
              px: 1.1,
              py: 0.5,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: 'inherit',
              color: value === b.key ? '#fff' : 'text.secondary',
              bgcolor: value === b.key ? INDIGO.main : 'transparent',
              '&:hover': {bgcolor: value === b.key ? INDIGO.main : 'action.hover'},
            }}
          >
            {b.label}
          </Box>
        ))}
      </Box>
    </Stack>
  )
}

BucketPicker.propTypes = {value: PropTypes.string, onChange: PropTypes.func.isRequired}

export default function PcisDashboard() {
  const [filters, setFilters] = useState(() => defaultFilters())
  const [tab, setTab] = useState(tabFromHash)
  const [live, setLive] = useState(false)
  const [bucket, setBucket] = useState('')
  const [lastRefresh, setLastRefresh] = useState(() => new Date())

  const {data: accounts} = useListPcisCourierAccountsQuery()

  /* Only the API-relevant keys become the query arg, and the object is memoised:
     RTK Query keys its cache on argument identity-by-value, so rebuilding an equal
     object each render is fine, but memoising keeps the eight child queries from
     re-evaluating their selectors on every parent render. */
  const query = useMemo(() => apiFilters(filters), [filters])

  /* Keep the hash in step with the tab so the view is linkable, and follow the
     hash when the user navigates back. */
  useEffect(() => {
    if (tabFromHash() !== tab) window.history.replaceState(null, '', `#${tab}`)
  }, [tab])

  useEffect(() => {
    const onHash = () => setTab(tabFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  /* Stamp the "updated" label on the same cadence the queries poll. */
  useEffect(() => {
    if (!live) return undefined
    setLastRefresh(new Date())
    const id = setInterval(() => setLastRefresh(new Date()), POLL_MS)
    return () => clearInterval(id)
  }, [live, query])

  const onFiltersChange = useCallback((next) => {
    setFilters(next)
    setLastRefresh(new Date())
  }, [])

  const pollMs = live ? POLL_MS : undefined
  const updatedAt = live ? lastRefresh.toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit', second: '2-digit'}) : null

  return (
    <>
      <PageTitle>PCIS — Dashboard</PageTitle>
      <PageHeader
        title='PCIS Dashboard'
        sub='Order volume, courier adoption and placement health across every Direct Integration (PCIS) account.'
      />

      <DashboardFilters
        filters={filters}
        onChange={onFiltersChange}
        accounts={accounts}
        live={live}
        onLiveChange={setLive}
        updatedAt={updatedAt}
      />

      <Stack direction='row' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5} sx={{borderBottom: 1, borderColor: 'divider', mb: 2.5}}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{
            minHeight: 42,
            '& .MuiTab-root': {minHeight: 42, textTransform: 'none', fontSize: 13.5, fontWeight: 650, px: 2},
            '& .MuiTabs-indicator': {height: 2, borderRadius: 2},
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
        {tab === 'overview' ? <Box sx={{pb: 0.75}}><BucketPicker value={bucket} onChange={setBucket} /></Box> : null}
      </Stack>

      {/* Only the active tab mounts. Each tab owns 2–4 queries; keeping them all
          mounted would fire ~14 requests per filter change for panels nobody is
          looking at. RTK Query's cache makes coming back cheap. */}
      {tab === 'overview' ? <TabOverview filters={query} bucket={bucket || undefined} pollMs={pollMs} /> : null}
      {tab === 'couriers' ? <TabCouriers filters={query} pollMs={pollMs} accounts={accounts} /> : null}
      {tab === 'failures' ? <TabFailures filters={query} pollMs={pollMs} onOpenOrders={() => setTab('orders')} /> : null}
      {tab === 'lifecycle' ? <TabLifecycle filters={query} pollMs={pollMs} /> : null}
      {tab === 'orders' ? <TabOrders filters={query} pollMs={pollMs} /> : null}
    </>
  )
}
